import type { MCPConnection } from '@cortex/types';

/**
 * Config-only auth status of an MCP connection.
 *
 *  - `ready`      — callable now (a `none`-auth row, or an `oauth` row that
 *                   already holds a keyring).
 *  - `needs-auth` — an `oauth` connection with no keyring: the workflow will
 *                   reference it, but a run can't actually call it until the
 *                   user connects it on the MCP Connections page.
 */
export type McpStatus = 'ready' | 'needs-auth';

/**
 * The single source-of-truth predicate for "can this MCP be called at runtime".
 * Mirrors what `resolveTools` (packages/node-types/.../resolve-tools.ts) can
 * actually use and the backend's caution rule in `workflow.service.ts`: a
 * `none`-auth row is always callable; an `oauth` row is only callable once it
 * has a keyring. We can't probe the live transport from the browser, so this is
 * deliberately a config-only check.
 *
 * IMPORTANT: never test `!hasKeyring` alone — `hasKeyring` is `false` for every
 * `authType: 'none'` row (see packages/types/src/mcp.ts), so that would flag a
 * perfectly callable no-auth connection. The `authType === 'oauth'` guard is
 * load-bearing.
 *
 * Accepts a structural subset so both full `MCPConnection` rows and the trimmed
 * `{ authType, hasKeyring }` shape the canvas eagerly loads can be passed.
 */
export function mcpStatus(row: Pick<MCPConnection, 'authType' | 'hasKeyring'>): McpStatus {
  if (row.authType === 'oauth' && !row.hasKeyring) return 'needs-auth';
  return 'ready';
}

/** Convenience boolean for the common "is this connection unusable" check. */
export function isMcpConnectionUnauthenticated(
  row: Pick<MCPConnection, 'authType' | 'hasKeyring'>,
): boolean {
  return mcpStatus(row) === 'needs-auth';
}

/**
 * Does this workflow node reference an MCP connection that isn't authenticated?
 * Only `llm-agent` nodes carry MCP references (in `config.toolRefs`, matched to
 * connections by NAME — see McpToolsEditor), so every other node type is `false`.
 * `unauthedConnectionNames` is the set of connection names that failed
 * `mcpStatus` (i.e. `needs-auth`). Pure + defensive about the untyped `config`
 * shape so the canvas decoration can call it per node without a guard.
 */
export function nodeReferencesUnauthenticatedMcp(
  nodeType: string,
  config: Record<string, unknown> | undefined,
  unauthedConnectionNames: ReadonlySet<string>,
): boolean {
  if (nodeType !== 'llm-agent' || unauthedConnectionNames.size === 0) return false;
  const toolRefs = Array.isArray(config?.toolRefs) ? (config.toolRefs as unknown[]) : [];
  return toolRefs.some((ref) => typeof ref === 'string' && unauthedConnectionNames.has(ref));
}
